/**
 * Performance utilities for monitoring and optimizing app performance
 */

// Web Vitals tracking
export interface WebVitals {
  lcp?: number // Largest Contentful Paint
  inp?: number // Interaction to Next Paint (replaces FID)
  cls?: number // Cumulative Layout Shift
  fcp?: number // First Contentful Paint
  ttfb?: number // Time to First Byte
}

/**
 * Measure and log Web Vitals
 * Call this in main.tsx after app initialization
 */
export function measureWebVitals(onPerfEntry?: (metric: any) => void) {
  if (onPerfEntry && typeof onPerfEntry === 'function') {
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      onCLS(onPerfEntry)
      onINP(onPerfEntry) // onFID replaced by onINP in web-vitals v3+
      onFCP(onPerfEntry)
      onLCP(onPerfEntry)
      onTTFB(onPerfEntry)
    }).catch(() => {
      // web-vitals not available, skip
    })
  }
}

/**
 * Performance timer for measuring operation duration
 */
export class PerformanceTimer {
  private startTime: number
  private label: string

  constructor(label: string) {
    this.label = label
    this.startTime = performance.now()
  }

  end(): number {
    const duration = performance.now() - this.startTime
    if (import.meta.env.DEV) {
      console.log(`[Performance] ${this.label}: ${duration.toFixed(2)}ms`)
    }
    return duration
  }

  endAndWarn(threshold: number): number {
    const duration = this.end()
    if (duration > threshold) {
      console.warn(`[Performance Warning] ${this.label} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`)
    }
    return duration
  }
}

/**
 * Create a performance timer
 */
export function startTimer(label: string): PerformanceTimer {
  return new PerformanceTimer(label)
}

/**
 * Debounce function for expensive operations
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(later, wait) as ReturnType<typeof setTimeout>
  }
}

/**
 * Throttle function for rate-limiting operations
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

/**
 * Check if code is running in production
 */
export function isProduction(): boolean {
  return import.meta.env.PROD
}

/**
 * Check if code is running in development
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

